import {
    Button,
    DataTable,
    Group,
    Icon,
    Tag
} from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import { ActionButtons, Filters, TagsWithPlus } from "components/custom";
import { SCRIPT_EDIT_ICON } from "components/svg";
import {
  CREATE_TEST_FORM,
    SCRIPT_ADD_QUESTIONS,
    SCRIPT_EDIT_QUESTIONS,
    SCRIPT_VIEW_QUESTIONS,
} from "constants/app-paths";
import { canUserModify } from "features/presentation/utils";
import { useGetAllQuestionsQuery } from "features/questions/apis";
import FeedbackModal from "features/quiz/takeQuiz/feedbackModal";
import {
    useGetPageTopicsQuery,
    useGetSearchListingScriptTopicsQuery,
    useGetSpecialitiesQuery,
    useUpdateQuestionStatsMutation
} from "features/script/api";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import theme from "utils/themeVars";
import { useGetAnsweredQuestionsQuery } from "../apis";
import moment from "moment";

const multipleModes = {
  specialties: true,
  scripts: true,
  topics: true,
  specialty: true,
  level: true,
};

function processObject(inputObj) {
  // Filter out keys with empty or undefined values
  const filteredObj = Object.fromEntries(
    Object.entries(inputObj).filter(
      ([_, value]) => value !== undefined && value !== ""
    )
  );

  // Convert comma-separated values to arrays
  const processedObj = Object.fromEntries(
    Object.entries(filteredObj).map(([key, value]) => {
      return [key, multipleModes?.[key] ? value.split(",") : value];
    })
  );

  return processedObj;
}

const AnsweredQuestions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  let queryObj = Object.fromEntries([...searchParams]);
  const [showModal, setShowModal] = useState(false);
  const [selectionQuestion, setSelectionQuestion] = useState(false);
  const [loading, setLoading] = useState(null);

  const user = useSelector((state) => state.app.user?.info);

  const getUserId = (queryObj) => {
    const keysToCheck = ["upVoted", "downVoted", "bookmarked"];
    if (keysToCheck.some((key) => queryObj.hasOwnProperty(key))) {
      return { userId: user?.user_code };
    }
  };

  const [handleUpdateStats, _] = useUpdateQuestionStatsMutation();

  const {
    data: allQuestions,
    isLoading: isLoadingQuestions,
    isFetching: isFetchingQuestions,
    error: questionsError,
    refetch: refetchQuestions,
  } = useGetAnsweredQuestionsQuery(
    {
      userId: user?.user_code,
      ...processObject({
        ...queryObj,
        ...(queryObj?.fromDate
          ? {
              fromDate: new Date(
                moment(queryObj?.fromDate).format("YYYY-MM-DD")
              ),
            }
          : {}),
        ...(queryObj?.toDate
          ? {
              toDate: new Date(moment(queryObj?.toDate).format("YYYY-MM-DD")),
            }
          : {}),
      }),
      // explanations: "",
      // scripts: "",
      // topics: "",
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: allScripts,
    isLoading: isLoadingScripts,
    isFetching: isFetchingScripts,
    error: scriptsError,
    refetch: refetchScripts,
  } = useGetPageTopicsQuery("");

  const { data: allTopics } = useGetSearchListingScriptTopicsQuery();
  const { data: specialties } = useGetSpecialitiesQuery();

  const onUpdateStats = async (question, options, key) => {
    setLoading({ id: question?.questionId, key: key });
    const res = await handleUpdateStats({
      questionId: question?.questionId,
      scriptId: question?.scripts?.[0],
      options: options,
      action: key,
    });
    if (res) {
      setLoading(null);
      refetchQuestions();
    }
  };

  const findScript = (scripts) => {
    if (allScripts?.length && scripts?.length) {
      return allScripts
        .filter(
          (e) =>
            scripts.includes(e.editor_template_id) || scripts?.includes(e?._id)
        )
        ?.map((e) => e?.page_name);
    }
    return ["-"];
  };

  const findTopic = (topics) => {
    if (allTopics?.data?.length && topics?.length) {
      return allTopics?.data
        .filter((topic) => topics.includes(topic._id))
        ?.map((e) => e?.PageTopics);
    }
    return ["-"];
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "key",
      className: "tableStartSpace",
      width: 100,
    },
    // {
    //   title: "Template Type",
    //   dataIndex: "templateType",
    // },
    // {
    //   title: "Specialty",
    //   dataIndex: "specialty",
    // },
    {
      title: "Question ID",
      dataIndex: "question_id",
    },
    {
      title: "Scripts",
      dataIndex: "script",
    },
    {
      title: "explanation",
      dataIndex: "explanation",
    },
    {
      title: "Topics",
      dataIndex: "topics",
    },
    {
      title: "Actions",
      dataIndex: "actions",
    },
  ];

  const checkTrue = (data, userId, key) => {
    if (data?.[key]?.length && data?.[key]?.includes(userId)) {
      return true;
    }
    return false;
  };

  const getActionsList = (e) => [
    {
      key: "view_questions",
      icon: <Icon icon="IoMdEye" />,
      color: canUserModify(user, e?.editor_id)
        ? theme["blue200"]
        : theme["gray500"],
      content: "View Question",
      title: "View Question",
      className: "pointer",
      onClick: () => {
        navigate(SCRIPT_VIEW_QUESTIONS.replace(":id", e?.questionId));
      },
    },
  ];

  const findDifficultyLevel = (item) => {
    const difficultyConfig = {
      easy: {
        label: "Easy",
        color: "#4ca64c",
      },
      medium: {
        label: "Medium",
        color: theme["warning-color"],
      },
      hard: {
        label: "Hard",
        color: theme["error-color"],
      },
    };

    const levels = Object.keys(difficultyConfig);
    for (const level of levels) {
      const userList = item?.questionDetails?.difficultyLevels?.[level];
      if (userList?.length && userList.includes(user?.user_code)) {
        return difficultyConfig[level];
      }
    }
    return null;
  };

  const tableData = useCallback(() => {
    return allQuestions?.length
      ? allQuestions?.map((e, index) => ({
          ...e,
          question_id: (
            <div style={{ display: "flex", gap: "10px" }}>
              <div>{e?.questionId}</div>
              {findDifficultyLevel(e)?.label && (
                <Tag
                  key={index}
                  style={{ marginBottom: "0.5vh" }}
                  color={findDifficultyLevel(e)?.color}
                >
                  {findDifficultyLevel(e)?.label}
                </Tag>
              )}
            </div>
          ),
          key: index + 1,
          script: <TagsWithPlus data={findScript(e?.scripts)} plusAfter={3} />,
          explanation: (
            <TagsWithPlus
              data={e?.explanation?.map((e) => e?.title)}
              plusAfter={3}
            />
          ),
          topics: <TagsWithPlus data={findTopic(e?.topics)} plusAfter={3} />,
          actions: <ActionButtons list={getActionsList(e)} />,
        }))
      : [];
  }, [allQuestions, allScripts, allTopics]);

  const filterList = useMemo(
    () => [
      {
        label: "none",
        list: allScripts?.length
          ? allScripts.map((e) => ({
              label: e?.page_name,
              value: e?.editor_template_id,
            }))
          : [],
        type: "select",
        key: "scripts",
        placeholder: "Select Script",
      },
      {
        label: "none",
        list: allTopics?.data?.length
          ? allTopics?.data?.map((e) => ({
              ...e,
              label: e?.PageTopics,
              value: e?._id,
            }))
          : [],
        type: "select",
        key: "topics",
        placeholder: "Select Topic",
      },
      {
        label: "Date Range",
        type: "dateRange",
        key: "dateRange",
        placeholder: "Select Date Range",
        onChange: (date) => {
          if (date?.length) {
            setSearchParams({
              ...queryObj,
              fromDate: moment(date[0]).format("YYYY-MM-DD"),
              toDate: moment(date[1]).format("YYYY-MM-DD"),
            });
          }
        },
      },
    ],
    [allScripts, allTopics?.data]
  );
  const handleAddFeedback = async (data) => {
    // const res = await handleUpdateStats({
    //   questionId: question?.questionId,
    //   scriptId: scriptId,
    //   options: options,
    //   action: key,
    // });
  };

  return (
    <PageLayout showSubMenu={true}>
      <Group
        gap="16px"
        className={"listings-content-adjustments"}
        style={{ marginTop: "-20px" }}
      >
        <FeedbackModal
          question={selectionQuestion}
          showModal={showModal}
          setShowModal={setShowModal}
          onSubmit={handleAddFeedback}
        />
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Filters list={filterList || []} />
          </div>
          <Button
            icon="AiOutlinePlus"
            text="Create Test"
            type="light"
            color="#1677FF"
            style={{
              backgroundColor: "#f0f8ff",
              borderColor: "#d6e4ff",
              color: "#1677FF",
              height: 48,
              fontWeight: 700,
            }}
            onClick={() => {
              navigate(CREATE_TEST_FORM);
            }}
          />
        </div>
        <DataTable
          pagination={{
            totalCount: allQuestions?.totalCount,
            pageOptions: [10, 25, 50],
          }}
          paginationOnBottom={true}
          onChangePage={(queryObj) => setSearchParams(queryObj)}
          columns={columns}
          data={tableData()}
          loading={isLoadingQuestions || isLoadingScripts}
          error={questionsError || scriptsError}
          onErrorRetry={() => {
            if (questionsError) refetchQuestions();
            if (scriptsError) refetchScripts();
          }}
          skeletonLoading={isFetchingQuestions || isFetchingScripts}
        />
      </Group>
    </PageLayout>
  );
};
  
  export default AnsweredQuestions;
  